import type { MapFeedItemDTO } from "../data/types";
import { fetchVenue } from "../Services/FeedService";
import { ApiError } from "../Services/http";
import { usePolledResource } from "./usePolledResource";

export type VenueViewState =
  | { status: "loading" }
  | { status: "found"; item: MapFeedItemDTO; stale: boolean }
  /** Servern sa 404: stället finns inte (eller är avstängt). */
  | { status: "notFound" }
  /** Inget svar och inget att visa. Säger ingenting om stället. */
  | { status: "error"; error: unknown };

/**
 * Ren beslutsfunktion. Det som inte får hända: att ett nätverksfel
 * presenteras som att stället inte finns, eller att ett ställe vi redan
 * visar försvinner för att en poll föll.
 */
export function venueViewState({
  item,
  error,
  isLoading,
}: {
  item: MapFeedItemDTO | null;
  error: unknown;
  isLoading: boolean;
}): VenueViewState {
  // 404 är det enda svaret som betyder "finns inte", också mitt i en poll.
  if (error instanceof ApiError && error.status === 404) return { status: "notFound" };

  if (item) return { status: "found", item, stale: Boolean(error) };
  if (error) return { status: "error", error };
  if (isLoading) return { status: "loading" };

  return { status: "loading" };
}

/** Ett ställe via GET /venues/:id, med samma 15 sekunders poll som kartan. */
export function useVenue(venueId: string | undefined) {
  const resource = usePolledResource(() => fetchVenue(venueId as string), {
    key: venueId ?? "",
    enabled: Boolean(venueId),
  });

  return {
    state: venueViewState({
      // Efter ett byte av ställe ligger förra stället kvar en render.
      item: resource.data?.venue.id === venueId ? resource.data : null,
      error: resource.error,
      isLoading: resource.isLoading,
    }),
    retry: resource.refresh,
  };
}
